//components/ApiAdminPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../context/ApiContext/ApiContext';
import { useCredentialsDropZone } from '../utils/useCredentialsDropZone'; // Adjust path
import { encryptCredentials } from '../crypt/cryptoUtils'; // Adjust path
import { useFlow } from '../context/FlowContext/FlowContext';
import { restructureObject, replaceUrlVariable } from './nodes/featureOverlay/moduleUtils/apiObjectFormatting'
import ApiFlowEngine from '../services/ApiFlowEngine';
import { csJobCreateObj } from './nodes/featureOverlay/uplynkCentralScripts/csJobCreateObj';
import { liveChannelCreateObj } from './nodes/featureOverlay/uplynkCentralScripts/liveChannelCreateObj';
import executeCallback from '../apiUtils/callbackHandler';
import { getDefaultCallbackConfig } from '../apiUtils/apiCallbackDefaults';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Trash2, Play, Pause, RefreshCw, Move, Edit, Save, X, Lock, Unlock, ArrowRight, Copy } from 'lucide-react';

const getCredentials = () => {
  // For browser environments
  if (typeof window !== 'undefined') {
    return {
      userId: process.env.REACT_APP_USER_ID || '',
      apiKey: process.env.REACT_APP_API_KEY || '',
      // Add other credentials you need
    };
  };
  return { userId: '', apiKey: '' }; // Default for non-browser
};

// --- Helper Function for File Download ---
const downloadFile = (content, filename, contentType = 'application/json;charset=utf-8;') => {
  try {
      const blob = new Blob([content], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a); // Append to body to ensure click works in all browsers
      a.click();
      document.body.removeChild(a); // Clean up
      URL.revokeObjectURL(url); // Free up memory
  } catch (error) {
      console.error("Download failed:", error);
      // Optionally show an error message to the user
      alert("Could not initiate file download.");
  }
};

const ApiAdminPage = ( { apiAdminPage, setApiAdminPage, showNotification, isLocked } ) => {
  const { 
    state,
    // setUplynkCentral,
    // updateUplynkCentral,
    appendUplynkJobState,
    clearUplynkCentral,
    moduleNodes, 
    executionOrder, 
    executionStatus,
    setExecutionOrder,
    // reorderExecution,
    removeModule,
    toggleModuleActive,
    setExecutionStatus,
    updateSharedParams,
    isExecuting,
    isCloudProcessRunning,
    setIsCloudProcessRunning,
    resetState
  } = useApi();

  const { setShowAllModuleOverlays } = useFlow();

  // API Call Engine
  // const uplynkCentralConfigObj = csJobCreateObj;
  const uplynkCentralConfigObj = liveChannelCreateObj;
  const engineRef = useRef(null);
  const timeout = 30000;

  // Local state
  // const [credentials, setCredentials] = useState({...creds});
  const [credentials, setCredentials] = useState(getCredentials());

  const [showCredentials, setShowCredentials] = useState(true);
  const [apiInterval, setApiInterval] = useState(1000);
  const [draggedItem, setDraggedItem] = useState(null);
  const [expanded, setExpanded] = useState({});
  // const [isCloudProcessRunning, setIsCloudProcessRunning] = useState(false);



  // const [isExecuting, setIsExecuting] = useState(false);
  // const [responses, setResponses] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedEndpoint, setExpandedEndpoint] = useState({});
  const [isEditing, setIsEditing] = useState({});
  // const [notificationMessage, setNotificationMessage] = useState(null);

  // const responses = Object.values(executionStatus.responses).flatMap(endpoints => Object.values(endpoints));
  const responses = executionStatus.responses;

  // Shared AbortController ref for ALL API calls
  const sharedAbortControllerRef = useRef(new AbortController());
  // Reset the abort controller when needed
  const resetAbortController = () => {
    sharedAbortControllerRef.current = new AbortController();
  };

  const {
    isDragging,
    dropError,
    handleDragFileOver,
    handleDragFileLeave,
    handleDropFile,
  } = useCredentialsDropZone(setCredentials);

  
  // --- Export Handler for Encrypted or Clear JSON ---
  const handleExport = async (exportType) => {
    // 1. Common Validation
    if (!credentials.userId || !credentials.apiKey) {
      alert("Please enter both User ID and API Key before exporting.");
      return;
    }
  
    let dataToProcess;
    let filename;
    // let successMessage = "File exported successfully.";
    let processingErrorPrefix;
  
    try {
      // 2. Prepare data and filename based on type
      if (exportType === 'plain') {
        dataToProcess = credentials;
        filename = 'credentials.json';
        processingErrorPrefix = "Plain JSON export failed";
      } else if (exportType === 'encrypted') {
        dataToProcess = await encryptCredentials(credentials, { mode: 'local-export' });
        filename = 'credentials-encrypted.json';
        processingErrorPrefix = "Encrypted export failed";
        console.log("Payload for encrypted export:", dataToProcess); // Keep for debugging if needed
      } else {
        console.error("Unknown export type:", exportType);
        alert("Invalid export type specified.");
        return;
      }
  
      // 3. Common Stringify and Download
      const jsonString = JSON.stringify(dataToProcess, null, 2);
      downloadFile(jsonString, filename);
      // alert(successMessage); // Optional success feedback
  
    } catch (error) {
      // 4. Common Error Handling
      console.error(`${processingErrorPrefix}:`, error);
      alert(`${processingErrorPrefix}: ${error.message || 'Unknown error'}`);
    }
  };

  useEffect(() => {
    console.log("responses: ", responses);
  }, [responses])

  useEffect(() => {
    console.log("state.uplynkCentral:", state.uplynkCentral);
  }, [state.uplynkCentral])

  // Add this in your ApiAdminPage.jsx
  useEffect(() => {
    console.log("");
    console.log(`%c[CLOUD PROCESS STATE] isCloudProcessRunning __: ${isCloudProcessRunning}`, 
      `color: ${isCloudProcessRunning ? 'green' : 'red'}; font-weight: bold;`);
    console.log("");
  }, [isCloudProcessRunning]);


  // Handle credential changes
  const handleCredentialChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  // Toggle module expansion
  const toggleExpand = (nodeId) => {
    setExpanded({
      ...expanded,
      [nodeId]: !expanded[nodeId]
    });
  };

  // Toggle endpoint expansion
  const toggleEndpointExpand = (nodeId, endpointKey) => {
    const key = `${nodeId}-${endpointKey}`;
    setExpandedEndpoint({
      ...expandedEndpoint,
      [key]: !expandedEndpoint[key]
    });
  };

  // Toggle edit mode
  const toggleEdit = (nodeId, endpointKey) => {
    const key = `${nodeId}-${endpointKey}`;
    setIsEditing({
      ...isEditing,
      [key]: !isEditing[key]
    });
  };

  // Handle drag start
  const handleDragStart = (e, nodeId) => {
    setDraggedItem({ type: 'node', id: nodeId });
    e.dataTransfer.effectAllowed = 'move';
    // Add a semi-transparent effect to the dragged item
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
  };

  // Handle drag over
  const handleDragOver = (e, nodeId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === nodeId) return;
    
    // Calculate new position
    const currentIndex = executionOrder.indexOf(draggedItem.id);
    const targetIndex = executionOrder.indexOf(nodeId);
    
    if (currentIndex !== targetIndex) {
      // Create new array without the dragged item
      const newOrder = executionOrder.filter(id => id !== draggedItem.id);
      // Insert dragged item at the target position
      newOrder.splice(targetIndex, 0, draggedItem.id);
      // Update execution order
      setExecutionOrder(newOrder);
    }
  };

  useEffect(() => {
    console.log("___isCloudProcessRunning", isCloudProcessRunning);
  }, [isCloudProcessRunning])


  const callbackUtilities = {
    showNotification,
    updateContextValue: (key, value) => {
      // Update context values - adapt this to your context system
      updateSharedParams({ [key]: value });
    },
    sharedAbortControllerRef,
    credentials,
    setIsCloudProcessRunning
  };

  // Fixed injectCallbackDependencies function
  const injectCallbackDependencies = (actionConfig, endpointPath) => {
    console.log("actionConfig: ", actionConfig);
    console.log("endpointPath: ", endpointPath);
    if (!actionConfig) return actionConfig;
    
    // Create a deep copy of the action config to avoid modifying the original
    const configCopy = {
      ...actionConfig,
      config: actionConfig.config ? {...actionConfig.config} : {}
    };
    
    // Handle different action types by modifying the copy
    switch (configCopy.action) {        
      case 'uplynkCentralInitiator':
        console.group("DEBUGGING: injectCallbackDependencies for uplynkCentralInitiator");
        console.log("BEFORE - Input actionConfig:", actionConfig);
        console.log("BEFORE - configCopy:", configCopy);
      
        // Determine which base config to use
        let configBase;
        if (endpointPath.includes('cloudslicer')) {
          configBase = csJobCreateObj;
        } else if (endpointPath.includes('live')) {
          configBase = liveChannelCreateObj;
        } else {
          configBase = csJobCreateObj; // Default fallback
        }
        
        // Create a deep copy of the base config
        const baseConfigCopy = JSON.parse(JSON.stringify(configBase));
        
        // MERGE the configType-specific parameters from actionConfig
        // This preserves custom configuration from the callback definition
        if (actionConfig.configType) {
          // Store the configType for reference
          baseConfigCopy.configType = actionConfig.configType;
        }
        
        // If actionConfig has its own config object, merge it with baseConfigCopy
        if (actionConfig.config) {
          Object.assign(baseConfigCopy, actionConfig.config);
        }
        
        // THEN add the callbacks and resources
        baseConfigCopy.callbacks = {
          setProcessRunning: setIsCloudProcessRunning,
          onProgressCallback: appendUplynkJobState,
          updateResults: updateSharedParams
        };
        
        baseConfigCopy.resources = {
          credentials,
          abortControllerRef: sharedAbortControllerRef
        };
        
        // Set the final config
        configCopy.config = baseConfigCopy;
        console.log("AFTER - Modified configCopy:", configCopy);
        console.groupEnd();
        
        if (baseConfigCopy.trigger) {
          // Fix requiresFields paths
          if (baseConfigCopy.trigger.requiresFields && Array.isArray(baseConfigCopy.trigger.requiresFields)) {
            baseConfigCopy.trigger.requiresFields = baseConfigCopy.trigger.requiresFields.map(field => {
              if (!field.includes('node.response')) {
                return 'node.response.data.' + field;
              }
              return field;
            });
            console.log('Fixed requiresFields to:', baseConfigCopy.trigger.requiresFields);
          }
        }
        if (baseConfigCopy.tracking) {
          console.log('BEFORE FIX - tracking idPath:', baseConfigCopy.tracking.idPath);
          // Fix tracking paths to match the node.response.data structure
          if (baseConfigCopy.tracking.idPath && !baseConfigCopy.tracking.idPath.includes('node.response')) {
            baseConfigCopy.tracking.idPath = 'node.response.data.' + baseConfigCopy.tracking.idPath;
            console.log('AFTER FIX - tracking idPath:', baseConfigCopy.tracking.idPath);
          }
        }
        
      
      
        break;

      case 'appendState':
        configCopy.config = {
          ...configCopy.config,
          appendFunction: appendUplynkJobState
        };
        break;
        
      case 'updateSharedParams':
        configCopy.config = {
          ...configCopy.config,
          updateFunction: updateSharedParams
        };
        break;
        
      case 'sendNotification':
        configCopy.config = {
          ...configCopy.config,
          notificationFunction: showNotification
        };
        break;
    }
    
    // Return the modified copy
    return configCopy;
  };
  
  

  // EXECUTE WORKFLOW
  const executeWorkflow = async (startIndex = 0) => {
    setShowAllModuleOverlays(true);
    clearUplynkCentral();
    updateSharedParams({ playerURL: null, isPlayerURLReady: false });
    // Reset abort controller before starting a new workflow
    resetAbortController();
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      if (!credentials.userId || !credentials.apiKey) {
        showNotification("Please enter your credentials");
        return;
      }
      // console.log("moduleNodes: ", moduleNodes);
      // console.log("moduleNodes object keys: ", Object.keys(moduleNodes));

      const activeExecutionOrder = executionOrder.filter(nodeId => 
        moduleNodes[nodeId]?.activeState !== false
      );

      if (activeExecutionOrder.length === 0) {
        showNotification(`All Modules are Inactive`);
        return
      }
      
      // Prepares Node API Calls
      const restructuredModuleNodes = restructureObject(moduleNodes);
      
      // Replaces Endpoint Variables with value from param object - removes the param key:value once completed.
      const variableReplaced = replaceUrlVariable(restructuredModuleNodes);
      
      // IF REQUIRED, ADD FUNCTION TO REPLACE ANY PARAM VALUES DEFINED VARIABLES WITH VALUE FROM STATE
      // 
      // 

      // Construct the proper flow configuration object
      const flowConfig = {
        credentials,
        apiInterval,
        moduleNodes: variableReplaced,
        executionOrder: activeExecutionOrder,
        executionStatus,
        abortController: sharedAbortControllerRef.current // Pass the shared abort controller
      };
      
      console.log("flowConfig", flowConfig);
      
      showNotification(`Executing Workflow`);
      setExecutionStatus(prevStatus => ({
        ...prevStatus,
        isExecuting: false,
        currentNodeId: null,
        currentEndpointIndex: null,
        errors: {},
        responses: {}
      }));

      // Initialize engine with the correct config
      engineRef.current = new ApiFlowEngine(flowConfig, {
        timeout,
        // Complete set of standardized callbacks for executeWorkflow
        callbacks: {
          beforeRequest: (data) => {
            console.log("Callback - beforeRequest callback fired:", data);
            
            // Update execution status using functional update
            setExecutionStatus(prevStatus => ({
              ...prevStatus,
              isExecuting: true,
              currentNodeId: data.moduleId,
              currentEndpointIndex: data.endpointKey
            }));
          },
          

    
          afterResponse: (data) => {
            console.log("Callback - afterResponse fired with data:", data);
          
            // 1. FIRST: Update the UI with response information
            // This adds the API response to our storage so we can display it
            setExecutionStatus(prevStatus => ({
              ...prevStatus,
              responses: {
                ...prevStatus.responses,
                [data.moduleId]: {
                  ...(prevStatus.responses[data.moduleId] || {}),
                  [data.endpointKey]: {
                    data: data.response,
                    timestamp: new Date(),
                    status: 'success'
                  }
                }
              }
            }));
          
            console.log("State update in afterResponse:", {
              moduleId: data.moduleId, 
              endpointKey: data.endpointKey,
              responseData: data.response
            });
            
            // 2. Create a data package with everything the callback needs
            const nodeData = {
              node: {
                ...moduleNodes[data.moduleId][data.endpointKey],
                response: data.response
              }
            };
            
            // 3. Find out what API was called
            const endpointPath = nodeData.node.path;
            // console.log("endpointPath", endpointPath);
            
            // 4. Get callback config from module or default
            const moduleCallbacks = moduleNodes[data.moduleId]?.callbacks || {};
            const callbackConfig = moduleCallbacks[endpointPath] || getDefaultCallbackConfig('afterResponse');
            
            // 5. Execute dynamic callback with proper config
            if (callbackConfig) {
              // Make a copy of the config we can modify
              let runtimeConfig = {...callbackConfig};
              // console.log("runtimeConfig", runtimeConfig);
              // console.log("runtimeConfig.actions", runtimeConfig.actions);
              
              // SOLUTION: We need to extract the afterResponse property if it exists
              // This handles the nested structure {afterResponse: {actions: [...]} }
              if (runtimeConfig.afterResponse && !runtimeConfig.actions) {
                // console.log("Fixing nested structure - extracting 'afterResponse' property");
                runtimeConfig = runtimeConfig.afterResponse;
                // console.log("Fixed structure:", runtimeConfig);
                // console.log("Now has actions array:", !!runtimeConfig.actions);
              }

              // 6. Handle multi-action or single action format
              if (Array.isArray(runtimeConfig.actions)) {
                // Map each action through our dependency injector
                runtimeConfig.actions = runtimeConfig.actions.map(action => 
                  injectCallbackDependencies({...action}, endpointPath)
                );
              } 
              // Single action format
              else if (runtimeConfig.action) {
                // IMPORTANT: Use assignment to capture the returned value
                runtimeConfig = injectCallbackDependencies({...runtimeConfig}, endpointPath);
              }
              console.log('DEBUG - ApiAdminPage: afterResponse');
              console.log('DEBUG - ApiAdminPage: nodeData: ', nodeData);
              console.log('DEBUG - ApiAdminPage: runtimeConfig: ', runtimeConfig);
              console.log('DEBUG - ApiAdminPage: callbackUtilities: ', callbackUtilities);
              // 7. Execute the callback with injected dependencies
              executeCallback('afterResponse', nodeData, runtimeConfig, callbackUtilities);
            }
          },
          
          onError: (data) => {
            console.log("Callback - onError callback fired:", data);
  
            // Update errors using functional update - standardized pattern
            setExecutionStatus(prevStatus => ({
              ...prevStatus,
              isExecuting: false,
              errors: {
                ...prevStatus.errors,
                [data.moduleId]: {
                  ...(prevStatus.errors[data.moduleId] || {}),
                  [data.endpointKey]: {
                    message: data.error.message,
                    timestamp: new Date(),
                    status: 'error'
                  }
                }
              }
            }));

            // Get the endpoint path
            const endpointPath = moduleNodes[data.moduleId][data.endpointKey].path;

            // First try to get callback config from the module itself (component-level config)
            // Then fall back to global registry if not found
            const moduleCallbacks = moduleNodes[data.moduleId]?.callbacks || {};
            const callbackConfig = moduleCallbacks[endpointPath] || getDefaultCallbackConfig(endpointPath);

            // Execute dynamic callback for error with proper config
            if (callbackConfig?.onError) {
              // Make a copy of the config we can modify
              const runtimeConfig = {...callbackConfig.onError};
              
              // Inject runtime dependencies for all action types (same helper function as in afterResponse)
              const injectDependencies = (actionConfig) => {
                if (!actionConfig) return actionConfig;
                
                // Create a copy to avoid modifying the original
                const configCopy = {...actionConfig};
                
                // Inject appropriate dependencies based on action type
                if (configCopy.action === 'appendState') {
                  configCopy.config = {
                    ...configCopy.config,
                    appendFunction: appendUplynkJobState
                  };
                }
                else if (configCopy.action === 'updateSharedParams') {
                  configCopy.config = {
                    ...configCopy.config,
                    updateFunction: updateSharedParams
                  };
                }
                
                return configCopy;
              };
              
              // Check if we're using the multi-action format
              if (Array.isArray(runtimeConfig.actions)) {
                // Inject dependencies for each action
                runtimeConfig.actions = runtimeConfig.actions.map(action => 
                  injectDependencies(action)
                );
              } 
              // Legacy single-action format
              else if (runtimeConfig.action) {
                injectDependencies(runtimeConfig);
              }
              
              // Execute the callback with injected dependencies
              executeCallback('onError', data, runtimeConfig, callbackUtilities);
            } else {
              // Default behavior if no custom callback is defined
              showNotification(`API Error: ${data.error.message}`);
            }

          },

          // Inside onComplete callback of executeWorkflow
          onComplete: () => {
            console.log("Callback - onComplete callback fired");

            // Update completion status using functional update
            setExecutionStatus(prevStatus => ({
              ...prevStatus,
              isExecuting: false,
              currentNodeId: null,
              currentEndpointIndex: null
            }));
            
            // Get the last executed module/endpoint
            const lastModule = executionStatus.currentNodeId;
            const lastEndpoint = executionStatus.currentEndpointIndex;
            
            if (lastModule && lastEndpoint) {
              // Get the endpoint path
              const endpointPath = moduleNodes[lastModule][lastEndpoint].path;
              
              // First try to get callback config from the module itself (component-level config)
              // Then fall back to global registry if not found
              const moduleCallbacks = moduleNodes[lastModule]?.callbacks || {};
              const callbackConfig = moduleCallbacks[endpointPath] || getDefaultCallbackConfig(endpointPath);
              
              // Execute dynamic callback for completion
              if (callbackConfig?.onComplete) {
                // Make a copy of the config we can modify
                const runtimeConfig = {...callbackConfig.onComplete};
                
                // Inject runtime dependencies for all action types
                const injectDependencies = (actionConfig) => {
                  if (!actionConfig) return actionConfig;
                  
                  // Create a copy to avoid modifying the original
                  const configCopy = {...actionConfig};
                  
                  // Inject appropriate dependencies based on action type
                  if (configCopy.action === 'uplynkCentralInitiator') {
                    configCopy.config = {
                      setProcessRunning: setIsCloudProcessRunning,
                      onProgressCallback: appendUplynkJobState,
                      updateResults: updateSharedParams,
                      credentials,
                      abortControllerRef: sharedAbortControllerRef
                    };
                  } else if (configCopy.action === 'appendState') {
                    configCopy.config = {
                      ...configCopy.config,
                      appendFunction: appendUplynkJobState
                    };
                  }
                  else if (configCopy.action === 'updateSharedParams') {
                    configCopy.config = {
                      ...configCopy.config,
                      updateFunction: updateSharedParams
                    };
                  }
                  
                  return configCopy;
                };
                
                // Check if we're using the multi-action format
                if (Array.isArray(runtimeConfig.actions)) {
                  // Inject dependencies for each action
                  runtimeConfig.actions = runtimeConfig.actions.map(action => 
                    injectDependencies(action)
                  );
                } 
                // Legacy single-action format
                else if (runtimeConfig.action) {
                  injectDependencies(runtimeConfig);
                }
                
                // Execute the callback with injected dependencies
                executeCallback('onComplete', 
                  { moduleId: lastModule, endpointKey: lastEndpoint }, 
                  runtimeConfig, 
                  callbackUtilities
                );
              } else {
                // Default notification
                showNotification("Workflow executed...");
              }
            } else {
              // Default notification
              showNotification("Workflow executed...");
            }
          },
          
          onInterrupt: () => {
            console.log("Callback - onInterrupt callback fired");
            
            // Update interrupt status using functional update
            setExecutionStatus(prevStatus => ({
              ...prevStatus,
              isExecuting: false,
              currentNodeId: null,
              currentEndpointIndex: null
            }));
            
            // Optionally show interrupted notification
            showNotification("onInterrupt - Workflow execution was interrupted");
          }
        }
      });
  
      console.log("Engine initialized with config:", flowConfig);
      
      // Set initial execution state
      setExecutionStatus(prevStatus => ({
        ...prevStatus,
        isExecuting: true,
        errors: {}  // Clear previous errors on new execution
      }));
      
      // Execute ONLY ONCE with proper error handling
      try {
        await engineRef.current.execute(startIndex);
        console.log("Execution completed successfully");
        console.log("Engine execution completed, current engine state:", engineRef.current);
      } catch (error) {
        console.error("Execution error:", error);
        
        // Update global error using functional update
        setExecutionStatus(prevStatus => ({
          ...prevStatus,
          isExecuting: false,
          errors: {
            ...prevStatus.errors,
            global: {
              message: error.message,
              timestamp: new Date(),
              status: 'error'
            }
          }
        }));
        
        showNotification(`Execution failed: ${error.message}`);
      }
    } catch (error) {
      console.error("Setup error:", error);
      
      // Update the execution status to show the error - standardized pattern
      setExecutionStatus(prevStatus => ({
        ...prevStatus,
        isExecuting: false,
        errors: {
          ...prevStatus.errors,
          global: {
            message: error.message,
            timestamp: new Date(),
            status: 'error'
          }
        }
      }));
      // ^^^ EXECUTE WORKFLOW
      
      // Show a notification about the error
      showNotification(`Setup failed: ${error.message}`);
    }
    
  };

  // Interrupt Execution
  const stopExecution = () => {
    // Abort all API calls using the shared controller
    sharedAbortControllerRef.current.abort();
    
    // Also interrupt the engine if it exists
    if (engineRef.current) {
      engineRef.current.interrupt();
    }
    
    // Show notification
    showNotification("Execution interrupted, all API calls aborted");
    
    // Reset abort controller for future operations
    resetAbortController();
  };



  // Helper function to get node name or ID
  const getNodeName = (nodeId) => {
    // console.log("getNodeName - nodeId: ", nodeId);
    const module = moduleNodes[nodeId];
    // console.log("getNodeName - module: ", module);
    if (module?.label) {
      // console.log("module?.ModuleName", module?.label);
      const shortID = nodeId.slice(0, 14) + '...'
      // return module.label + ': ' + shortID;
      return (
        <>
          <span className="font-extrabold">{module.label}</span>: {shortID}
        </>
      );  
    } else {
      return (
        <>
          {/* <span className="text-gray-400 font-sans text-xs">Node: </span> */}
          {nodeId.slice(0, 28)}...
        </>
      );
    }
  };
  

  // Format JSON for display
  const formatJson = (json) => {
    try {
      return JSON.stringify(json, null, 2);
    } catch (e) {
      return String(json);
    }
  };



  {/* Helper function to detect API errors */}
  const hasApiError = (response) => {
    return response.status === 'success' && 
          response.data && 
          (response.data.data.error == '1' || 
            (typeof response.data === 'object' && 'msg' in response.data && response.data.msg.toLowerCase().includes('error')));
  };


  return (
    <>
      <button
        onClick={() => setApiAdminPage(!apiAdminPage)}
        className={`fixed z-[100] top-8 mt-0.5 bg-gradient-to-r from-[hsl(323,96%,10%)] to-[hsl(323,96%,45%)] py-1 rounded-r-md shadow-lg transition-all duration-500 ease-in-out -left-1'`}
        aria-label={apiAdminPage ? "Close admin panel" : "Open admin panel"}
      >
        {apiAdminPage ? (
          <ChevronLeft className="h-5 w-5 text-white" />
        ) : (
          <ChevronRight className="h-5 w-5 text-white" />
        )}
      </button>

      {Object.keys(moduleNodes).length > 0 &&
        <div className='z-50 fixed bottom-0 text-center w-full flex flex-row justify-center items-center pointer-events-none'>
          <button
            onClick={() => {
              executeWorkflow(0)
            }}
            disabled={isExecuting || isCloudProcessRunning || !isLocked}
            className={`rounded-md w-48 mb-4 py-2 text-lg font-semibold transition-all border border-gray-500 duration-200 pointer-events-auto
              ${isExecuting || isCloudProcessRunning || !isLocked 
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                : 'bg-[#22425e] text-white hover:bg-[#c8047d]'}`}
            title={!isLocked ? "Lock Workflow to Execute" : "Execute Workflow"}
          >
            {isLocked ? isExecuting || isCloudProcessRunning ? (
              <>
                Executing...
              </>
            ) : (
              <>
                Execute Workflow
              </>
            ) : (
              <>
                Lock to Execute
              </>
            )}
          </button>
        </div>
      }

      {/* <div className="min-w-96 w-1/3 fixed z-50 left-0 top-0 bottom-0 flex flex-col h-screen text-white backdrop-blur-sm bg-gradient-to-r from-[rgb(17,24,39,0.5)] to-[rgba(57,46,192,0.5)] overflow-hidden border-r-2 border-[rgba(57,46,192,0.7)]"> */}
      <div 
          className={`min-w-96 w-[28%] fixed z-50 left-0 top-0 bottom-0 flex flex-col h-screen text-white backdrop-blur-sm bg-gradient-to-r from-[rgb(17,24,39,0.5)] to-[rgba(57,46,192,0.5)] overflow-hidden border-r-2 border-[rgba(57,46,192,0.7)]
          transform transition-all duration-500 ease-in-out 
          ${apiAdminPage ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}`}
      >
        {/* Header */}
        <header className="relative py-2 text-center bg-black/30 backdrop-blur-sm border-b border-[#302ec0]">
          <div className="container mx-auto">
            <h1 className="text-2xl font-bold bg-clip-text text-[#FB1FA7]">
              API Workflow Admin
            </h1>
            <p className="text-gray-200 text-sm mt-1">Manage and execute API workflows</p>
          </div>
          <button className="absolute m-0.5 top-0 right-0 text-xs font-bold border-x border-y rounded-md border-red-800 bg-[#5e0f0f] w-[5rem] py-0.5 hover:border-red-600 hover:font-semibold active:bg-red-900 transition-colors duration-200"
            onClick={() => {
              setCurrentPage(0);
              resetState();
              setExecutionStatus(prevStatus => ({
                ...prevStatus,
                isExecuting: false,
                currentNodeId: null,
                currentEndpointIndex: null,
                errors: {},
                responses: {}
              }));
            }}
          >
                  Reset State
                </button>
        </header>

        {/* Main content */}
        <main className="flex-grow p-2 container mx-auto overflow-y-auto overflow-x-hidden">
          <div className="flex flex-col gap-3">
            {/* Credentials and Settings */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-xl px-3 py-2 border border-gray-600 bg-opacity-50">
              
              {/* Credentials Header */}
              <h2 className="relative text-[1.05rem] font-semibold mb-1 flex items-center justify-between cursor-pointer" onClick={() => setShowCredentials(!showCredentials)}>
                <div className="flex items-center select-none">
                  <Lock className="w-3 h-3 mr-2" />
                  Credentials
                </div>

                {/* Export Buttons Container */}
                <div className="relative group py-0.5"> {/* Keep this relative for positioning the text */}
                  {/* Buttons Layer (Handles Clicks) */}
                  <div className='absolute inset-0 flex rounded bg-blue-950 border border-blue-900'> {/* Use inset-0 and flex */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // <--- Stop the event from bubbling up!
                        handleExport('plain');  // Call your original handler (no need to pass 'e' if it doesn't use it)
                      }}
                      className="flex-1 h-full outline-none bg-transparent hover:bg-gradient-to-l hover:from-transparent hover:via-pink-900 hover:to-[#c8047d] hover:border-[#fb50ba] rounded-l transition-colors duration-150"
                      aria-label="Export Plain JSON" // Add aria-label for accessibility
                      title="Export Clear Credentials" // Add aria-label for accessibility
                    >
                       {/* You can add an icon or text inside if needed, or leave empty for visual button */}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // <--- Stop the event from bubbling up!
                        handleExport('encrypted'); // Call your original handler
                      }}
                      className="flex-1 h-full outline-none bg-transparent hover:bg-gradient-to-r hover:from-transparent hover:via-pink-900 hover:to-[#c8047d] hover:border-[#fb50ba] rounded-r transition-colors duration-150"
                      aria-label="Export Encrypted Credentials" // Add aria-label for accessibility
                      title="Export Encrypted Credentials" // Add aria-label for accessibility
                    >
                       {/* You can add an icon or text inside if needed, or leave empty for visual button */}
                    </button>
                  </div>
                  {/* Text Layer (Visual Only) */}
                  <p className='relative text-sm text-gray-300 w-24 text-center pointer-events-none group-hover:text-white group-hover:font-bold transition-colors duration-150 ease-in-out'> {/* <--- Added group-hover:text-white and transition */}
                    Export
                  </p>
                </div>

                {/* Chevron Toggle Indicator */}
                {showCredentials ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </h2>

              {/* Credentials Details */}
              {showCredentials && (
                <div
                  // Add the drop handlers here
                  onDrop={handleDropFile}
                  onDragOver={handleDragFileOver}
                  onDragLeave={handleDragFileLeave}
                  // Add conditional styling for visual feedback
                  className={`relative space-y-2 rounded-md border-2 transition-colors duration-200 ease-in-out mb-4
                              ${isDragging
                                ? 'border-indigo-500 bg-gray-700/50 border-dashed' // Style when dragging over
                                : 'border-transparent' // Default transparent border (or border-gray-700 if you want it always visible)
                              }`}
                >
                  {/* Optional: Add text hint only when dragging */}
                  {isDragging && (
                    <p className="float-right w-full mx-auto text-center text-indigo-300 text-sm pointer-events-none">
                      Drop JSON file here to populate fields
                    </p>
                  )}

                  {/* User ID Input */}
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm pointer-events-none">User ID</label> {/* Added pointer-events-none to label */}
                    <input
                      type="text"
                      name="userId"
                      value={credentials.userId}
                      onChange={handleCredentialChange}
                      className="w-full text-[0.8em] bg-gray-900 border border-gray-700 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter User ID or drop file"
                    />
                  </div>
                  {/* API Key Input */}
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm pointer-events-none">API Key</label> {/* Added pointer-events-none to label */}
                    <input
                      type="password"
                      name="apiKey"
                      value={credentials.apiKey}
                      onChange={handleCredentialChange}
                      className="w-full text-[0.8em] bg-gray-900 border border-gray-700 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter API Key or drop file"
                    />
                  </div>
                </div>
              )}

              {/* Display Drop Error (Positioned below the drop zone/inputs) */}
              {dropError && (
                <p className="text-red-500 text-sm mt-0 mb-3 text-center">{dropError}</p> // Adjusted margin
              )}

              <div className={`w-full mt-2 mb-2 ${!showCredentials ? 'border-t border-gray-700' : '' }`}></div>

              {/* API Options */}
              <h2 className="text-[1.05rem] font-semibold mb-1">API Settings</h2>
              <div>
                <div className="flex justify-between items-center mb-0.5">
                  <label className="text-gray-400 text-sm">API Call Interval</label>
                  <span className="font-mono text-xs text-indigo-300">{(apiInterval/1000).toFixed(1)}s</span>
                </div>
                <input 
                  type="range" 
                  value={apiInterval}
                  onChange={(e) => setApiInterval(parseInt(e.target.value))}
                  min="100"
                  max="10000"
                  step="100"
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0.1s</span>
                  <span>10s</span>
                </div>
              </div>

              <div className="mt-2">
                <button
                  onClick={() => {
                    executeWorkflow(0)
                  }}
                  disabled={isExecuting || isCloudProcessRunning || !isLocked}
                  className={`w-full flex items-center justify-center rounded-md px-4 py-1.5 font-semibold transition-all border border-gray-500 duration-200
                    ${isExecuting || isCloudProcessRunning || !isLocked 
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                      : 'bg-[#22425e] text-white hover:bg-[#c8047d]'}`}
                  title={!isLocked ? "Lock Workflow to Execute" : "Execute Workflow"}
                >
                  {isLocked ? isExecuting || isCloudProcessRunning ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Executing...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" />
                      Execute Workflow
                    </>
                  ) : (
                    <>
                      {/* <Play className="w-5 h-5 mr-2" /> */}
                      Lock Workflow to Execute
                    </>
                  )}
                </button>
                <button
                  onClick={stopExecution}
                  disabled={!isExecuting || !isCloudProcessRunning}
                  className={`mt-3 w-full flex items-center text-sm justify-center rounded-md px-4 py-1 text-white font-semibold transition-all border border-gray-500 duration-200`}
                >
                  <Pause className="w-4 h-4 mr-2" /> Stop Execution
                </button>
              </div>
            </div>

            {/* Execution Order */}
            <div className="flex flex-col">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-xl px-3 py-2 border border-gray-600">
                <h2 className="text-[1.05rem] font-semibold mb-1">Execution Order</h2>
                
                {executionOrder.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No modules configured for execution
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {executionOrder.map((nodeId, index) => (
                      <li 
                        key={nodeId}
                        className={`bg-gray-900/70 rounded-lg border border-gray-700 overflow-hidden transition-all duration-200
                          ${draggedItem?.id === nodeId ? 'opacity-50' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, nodeId)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, nodeId)}
                      >
                        {/* Module + Endpoint Rendering */}
                        <div 
                          className="flex flex-col py-2"
                        >
                          <div
                            className="relative flex items-center justify-between cursor-pointer px-3"
                            onClick={() => toggleExpand(nodeId)}                        
                          >
                            <Move className="absolute -top-1.5 left-0.5 w-2.5 h-2.5 text-gray-500" />
                            <div className="flex items-center">
                              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-700 text-white text-xs mr-2">
                                {index + 1}
                              </span>
                              <span className="font-medium text-[0.8em] font-sans text-gray-300">{getNodeName(nodeId)}</span>
                            </div>
                            {expanded[nodeId] ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}

                          </div>
                          
                          {/* activeState && readyState */}
                          <div className="flex justify-between items-center mt-2 px-3">
                            <span 
                              className={`w-16 text-center py-0.5 text-[0.65em] rounded cursor-pointer ${
                                moduleNodes[nodeId]?.activeState 
                                  ? 'bg-indigo-900/60 text-indigo-100' 
                                  : 'bg-gray-700/60 text-gray-400'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleModuleActive(nodeId);
                              }}
                            >
                              {moduleNodes[nodeId]?.activeState ? 'ACTIVE' : 'INACTIVE'}
                            </span>


                            <span className={`px-1.5 py-0.5 text-[0.65em] rounded ${
                              moduleNodes[nodeId]?.readyState 
                                ? 'bg-emerald-900/60 text-emerald-300' 
                                : 'bg-red-900/60 text-red-400'
                            }`}>
                              {moduleNodes[nodeId]?.readyState ? 'READY' : 'NOT READY'}
                            </span>

                            <div className="flex items-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeModule(nodeId);
                                }}
                                className="text-gray-400 hover:text-red-400 mr-0.5"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                        </div>

                        {expanded[nodeId] && moduleNodes[nodeId] && (
                          <div className="pl-1 pr-1 pb-3">
                            <div className="space-y-2 ml-2">
                              {Object.entries(moduleNodes[nodeId])
                                .filter(([key]) => key.startsWith('endpoint_'))
                                .map(([endpointKey, endpoint]) => {
                                  const isExpanded = expandedEndpoint[`${nodeId}-${endpointKey}`];
                                  return (
                                    <div 
                                      key={`${nodeId}-${endpointKey}`}
                                      className="bg-gray-800 rounded border border-gray-700"
                                    >
                                      <div 
                                        className="flex items-center justify-between py-2 pl-1 pr-2 cursor-pointer"
                                        onClick={() => toggleEndpointExpand(nodeId, endpointKey)}
                                      >
                                        <div className="flex items-center">
                                          <span className={`w-14 text-center text-[0.6em] font-bold uppercase py-1 rounded mr-1 ${
                                            endpoint.method === 'GET' ? 'bg-green-900/50 text-green-400' : 
                                            endpoint.method === 'POST' ? 'bg-blue-900/50 text-blue-400' :
                                            endpoint.method === 'PUT' ? 'bg-purple-900/50 text-purple-400' :
                                            endpoint.method === 'PATCH' ? 'bg-yellow-900/50 text-yellow-400' :
                                            endpoint.method === 'DELETE' ? 'bg-red-900/50 text-red-400' :
                                            'bg-gray-900/50 text-gray-400'
                                          }`}>
                                            {endpoint.method}
                                          </span>
                                          <span 
                                            className="text-[0.7em] font-mono"
                                            title={endpoint.path}
                                            style={{
                                              display: 'inline-block'
                                            }}
                                          >
                                            {endpoint.path.length > 30 
                                              ? `...${endpoint.path.substring(endpoint.path.length - 25)}`
                                              : endpoint.path
                                            }
                                          </span>
                                        </div>
                                        <div className=''>
                                          {isExpanded ? (
                                            <ChevronUp className="w-4 h-4 text-gray-400" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                          )}
                                        </div>
                                      </div>
                                      
                                      {isExpanded && (
                                        <div className="p-3 border-t border-gray-700">
                                          <div className="text-xs text-gray-400 mb-2">
                                            Root URL: {endpoint.root_url}
                                          </div>
                                          <div className="text-xs font-mono bg-gray-900 p-3 rounded">
                                            <pre className="whitespace-pre-wrap">
                                              {formatJson(endpoint.params)}
                                            </pre>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Response Viewer */}
            <div className="flex flex-col overflow-hidden">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-xl px-3 py-2 border border-gray-600">
                <h2 className="text-[1.05rem] font-semibold">API Responses</h2>
                
                {/* Check if responses object is empty */}
                {Object.keys(responses).length === 0 ? (
                  <div className="text-center pt-4 pb-4 text-gray-400">
                    Execute the workflow to see results.
                  </div>
                ) : (
                  <div>
                    {/* Node pagination controls */}
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-sm text-gray-400">
                        Showing node {currentPage + 1} of {Object.keys(responses).length}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                          disabled={currentPage === 0}
                          className={`p-1 rounded ${currentPage === 0 ? 'text-gray-600' : 'text-gray-300 hover:bg-gray-700'}`}
                        >
                          <ChevronUp className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(Object.keys(responses).length - 1, prev + 1))}
                          disabled={currentPage >= Object.keys(responses).length - 1}
                          className={`p-1 rounded ${currentPage >= Object.keys(responses).length - 1 ? 'text-gray-600' : 'text-gray-300 hover:bg-gray-700'}`}
                        >
                          <ChevronDown className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Display current node's responses */}
                    {/* {Object.keys(responses)[currentPage] && ( */}
                    {Object.keys(responses).length > 0 && currentPage < Object.keys(responses).length && (
                      <div className="bg-gray-900 rounded-lg border border-gray-600 px-3 py-2">
                        {/* Node header with ID */}
                        <div className="pb-0 mb-2.5">
                          <h3 className="font-medium text-[0.8em] font-sans text-gray-300"
                            title={Object.keys(responses)[currentPage]}
                          >
                            {getNodeName(Object.keys(responses)[currentPage])}
                          </h3>
                        </div>
                        
                        {/* Responses: Map through all endpoints for this node */}
                        {Object.entries(responses[Object.keys(responses)[currentPage]] || {}).map(([endpointKey, response]) => {
                          // Safety check for response
                          if (!response) return null;
                          
                          const apiError = hasApiError(response);
                          return (
                            <div key={endpointKey} className="mb-4 last:mb-0">
                              {/* Expandable endpoint tile */}
                              <div 
                                className={`border ${apiError ? 'border-red-600' : 'border-gray-700'} rounded-lg overflow-hidden ${apiError ? 'bg-red-900/10' : ''}`}
                                aria-expanded={expandedEndpoint[`${Object.keys(responses)[currentPage]}-${endpointKey}`]}
                              >
                                {/* Endpoint header (always visible) */}
                                <div 
                                  className={`flex justify-between items-center pr-2 ${apiError ? 'bg-gray-800/80' : 'bg-gray-800'} cursor-pointer hover:bg-gray-750`}
                                  onClick={() => toggleEndpointExpand(Object.keys(responses)[currentPage], endpointKey)}
                                  title={apiError ? `API Error: ${response.data?.msg || JSON.stringify(response.data?.error)}` : null}
                                >
                                  <div className="flex items-center justify-between flex-grow p-2">
                                    <span className="text-[0.8em] font-medium mr-2 text-gray-400">{endpointKey}</span>
                                    
                                    {/* Method + HTTP Status as combined badge with safety checks */}
                                    <div className="flex items-center gap-1">
                                      {moduleNodes?.[Object.keys(responses)?.[currentPage]]?.[endpointKey]?.method && (
                                        <span className={`text-[0.7em] font-bold uppercase px-2 py-0.5 rounded ${
                                          moduleNodes[Object.keys(responses)[currentPage]][endpointKey].method === 'GET' 
                                            ? 'bg-green-900/50 text-green-400' 
                                            : moduleNodes[Object.keys(responses)[currentPage]][endpointKey].method === 'POST' 
                                              ? 'bg-blue-900/50 text-blue-400'
                                              : 'bg-yellow-900/50 text-yellow-400'
                                        }`}>
                                          {moduleNodes[Object.keys(responses)[currentPage]][endpointKey].method}
                                        </span>
                                      )}
                                      
                                      <span className={`text-[0.7em] font-bold uppercase px-2 py-0.5 rounded ${
                                        response.status === 'success' 
                                          ? apiError 
                                            ? 'bg-amber-800/50 text-amber-300' 
                                            : 'bg-green-900/50 text-green-400' 
                                          : 'bg-red-900/50 text-red-400'
                                      }`}>
                                        {response.status === 'success'
                                          ? apiError 
                                            ? 'HTTP OK' 
                                            : 'HTTP OK' 
                                          : 'HTTP ERR'}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center text-gray-400">
                                    <span className="text-xs mr-3">
                                      {new Date(response.timestamp).toLocaleString()}
                                    </span>
                                    {expandedEndpoint[`${Object.keys(responses)[currentPage]}-${endpointKey}`] 
                                      ? <ChevronUp className="w-4 h-4" /> 
                                      : <ChevronDown className="w-4 h-4" />
                                    }
                                  </div>
                                </div>
                                
                                {/* Endpoint content (expandable) with safety checks */}
                                {expandedEndpoint[`${Object.keys(responses)[currentPage]}-${endpointKey}`] && (
                                  <div className="px-2 border-t border-gray-700">
                                    <div className='flex flex-row items-center justify-between py-2'>
                                      {/* API Error message (if present) */}
                                      {apiError && (
                                        <div className="text-red-400 text-xs pl-1">
                                          <strong>API Error</strong> {response.data?.msg || JSON.stringify(response.data?.error)}
                                        </div>
                                      )}

                                      {/* Copy button */}
                                      <button 
                                        className="text-gray-400 hover:text-white flex items-center text-xs"
                                        onClick={() => {
                                          navigator.clipboard.writeText(formatJson(response.data || {}));
                                          showNotification("Response copied to clipboard");
                                        }}
                                      >
                                        <Copy className="w-3 h-3 mr-1" />
                                        Copy Response
                                      </button>
                                    </div>
                                    
                                    {/* Path/URL if available with optional chaining */}
                                    {moduleNodes?.[Object.keys(responses)?.[currentPage]]?.[endpointKey]?.path && (
                                      <div className="mb-2 bg-gray-800 p-2 rounded text-xs font-mono break-all">
                                        {moduleNodes?.[Object.keys(responses)?.[currentPage]]?.[endpointKey]?.root_url}
                                        {moduleNodes?.[Object.keys(responses)?.[currentPage]]?.[endpointKey]?.path}
                                      </div>
                                    )}
                                    
                                    {/* Response data with safe access */}
                                    <div className="bg-gray-800 p-2 rounded-lg border border-gray-700">
                                      <pre className="text-xs font-mono whitespace-pre-wrap overflow-auto max-h-96">
                                        {formatJson(response.data || {})}
                                      </pre>
                                    </div>
                                    
                                    {/* Show duration if available */}
                                    {response.duration && (
                                      <div className="mt-2 text-right text-xs text-gray-500">
                                        Response time: {response.duration}ms
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}

                      </div>
                    )}
                  </div>
                )}
              </div>


            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-black/30 backdrop-blur-sm border-t border-gray-800 h-4">
          {/* <div className="container mx-auto px-6 text-center text-gray-400 text-sm">
            Uplynk API Workflow Manager © {new Date().getFullYear()}
          </div> */}
        </footer>
      </div>
    </>
  );
};

export default ApiAdminPage;